(() => {
  if (customElements.get('rtd-quote')) return;

  const store = {
    get(k) { try { return JSON.parse(window.sessionStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  };

  const num = (v) => parseFloat(String(v).replace(/\s/g, '').replace(',', '.'));

  const PAIR = /(\d+)\s*(?:stück|stk\.?|pcs|x)?\s*:\s*(\d+(?:[.,]\d{1,2})?)/gi;

  function parsePairs(text) {
    const out = [];
    String(text || '').replace(PAIR, (m, q, p) => { out.push({ min: parseInt(q, 10), price: num(p) }); return m; });
    return out.filter((x) => x.min > 0 && x.price > 0).sort((a, b) => a.min - b.min);
  }

  function parseTiers(text) {
    const base = [];
    const overrides = [];
    String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((line) => {
      if (line.includes('|')) {
        const [left, right] = line.split('|');
        const terms = left.split('+').map((x) => x.trim().toLowerCase()).filter(Boolean);
        if (terms.length) overrides.push({ terms, tiers: parsePairs(right) });
      } else {
        parsePairs(line).forEach((x) => base.push(x));
      }
    });
    base.sort((a, b) => a.min - b.min);
    return { base, overrides };
  }

  const QTY_LINE = /^(st(?:ü|ue)ckzahl|menge|mengen|qty|quantity|anzahl)\b/i;

  const CELL = /\d+(?:[.,]\d{1,2})?|[^\s;|,\t][^;|\t]*/g;

  function splitCells(text) {
    const raw = String(text || '').trim();
    if (!raw) return [];
    if (/[;|\t]/.test(raw)) return raw.split(/[;|\t]+/).map((x) => x.trim()).filter((x) => x !== '');
    return (raw.match(CELL) || []).map((x) => x.trim()).filter((x) => x !== '');
  }

  function parseGrid(text) {
    const sections = [];
    let cur = null;
    const open = (terms, derive, full) => {
      cur = { terms: terms, full: full || terms.join(' + '), derive: derive || null, cols: [], rows: {} };
      sections.push(cur);
      return cur;
    };
    String(text || '').split(/\r?\n/).forEach((raw) => {
      const line = raw.trim();
      if (!line || line[0] === '#') return;
      if (line[0] === '[') {
        const close = line.indexOf(']');
        if (close < 0) return;
        const name = line.slice(1, close).trim();
        const rest = line.slice(close + 1).trim();
        let derive = null;
        const m = rest.match(/^=\s*(.+?)\s*[x×*]\s*(\d+(?:[.,]\d+)?)$/i);
        if (m) derive = { from: m[1].trim().toLowerCase(), factor: num(m[2]) };
        open(name.split('+').map((x) => x.trim().toLowerCase()).filter(Boolean), derive, name.trim().toLowerCase());
        return;
      }
      const colon = line.indexOf(':');
      if (colon < 0) return;
      const label = line.slice(0, colon).trim();
      const rest = line.slice(colon + 1);
      if (!cur) open([], null, '');
      if (QTY_LINE.test(label)) {
        cur.cols = splitCells(rest).map((x) => parseInt(String(x).replace(/[^\d]/g, ''), 10)).filter((x) => x > 0);
        return;
      }
      cur.rows[label.toLowerCase()] = { label: label, cells: splitCells(rest) };
    });
    return sections.filter((sec) => Object.keys(sec.rows).length || sec.derive);
  }

  function gridTiers(sec, cols, row) {
    const out = [];
    row.cells.forEach((cell, i) => {
      const q = cols[i];
      const p = num(cell);
      if (q > 0 && p > 0) out.push({ min: q, price: Math.round(p * 100) / 100 });
    });
    return out.sort((a, b) => a.min - b.min);
  }

  class RtdQuote extends HTMLElement {
    connectedCallback() {
      let cfg = {};
      try { cfg = JSON.parse(this.querySelector('[data-config]').textContent); } catch (e) { cfg = {}; }
      this.variants = cfg.variants || [];
      if (!this.variants.length) { this.hidden = true; return; }
      this.pricing = parseTiers(cfg.tiers);
      this.grid = parseGrid(cfg.sizes);
      this.sizeStep = this.querySelector('[data-size-step]');
      this.sizeButtons = this.sizeStep ? Array.from(this.sizeStep.querySelectorAll('[data-size]')) : [];
      this.size = this.sizeButtons.length ? this.sizeButtons[0].dataset.size : '';
      this.quoteValues = String(cfg.quote || '').split(/[,\n]/).map((x) => x.trim().toLowerCase()).filter(Boolean);
      this.included = String(cfg.included || '').split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
      this.freeOptionNames = String(cfg.freeOptions || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
      this.noMold = String(cfg.noMold || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
      this.moldTerm = String(cfg.moldTerm || '').trim();
      this.productInfo = String(cfg.productInfo || '').toLowerCase();
      this.designLabel = cfg.designLabel || 'Design';
      this.designOwn = cfg.designOwn || 'Design vorhanden';
      this.designService = cfg.designService || 'Design erstellen lassen';
      this.designRow = cfg.designRow || 'Designerstellung';
      this.designPrice = parseFloat(String(cfg.designPrice || '').replace(',', '.')) || 0;
      this.shipping = parsePairs(cfg.shipping).map((x) => ({ min: x.min, price: x.price }));
      String(cfg.shipping || '').split(/[,\n]/).forEach((part) => {
        const m = part.match(/(\d+)\s*:\s*0(?:[.,]0{1,2})?\s*$/);
        if (m) this.shipping.push({ min: parseInt(m[1], 10), price: 0 });
      });
      this.shipping.sort((a, b) => a.min - b.min);
      this.shippingLabel = cfg.shippingLabel || 'Versand';
      this.shippingFree = cfg.shippingFree || 'kostenlos';
      this.presets = String(cfg.presets || '').split(/[,;\n]/).map((x) => parseInt(String(x).replace(/[^\d]/g, ''), 10)).filter((x) => x > 0).sort((a, b) => a - b);
      this.quoteAbove = cfg.quoteAbove || '';
      this.sizeStepUnit = parseInt(cfg.sizeStep || 5, 10) || 5;
      this.sizeUnit = cfg.sizeUnit || 'mm';
      this.max = this.presets.length ? this.presets[this.presets.length - 1] : parseInt(this.dataset.max || '2000', 10);
      this.fmt = new Intl.NumberFormat(this.dataset.locale || 'de', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.$ = (sel) => this.querySelector(sel);

      this.steps = Array.from(this.querySelectorAll('[data-option]')).map((fs) => ({
        index: parseInt(fs.dataset.option, 10),
        name: (fs.querySelector('legend') ? fs.querySelector('legend').textContent.replace(/^\s*\d+\s*/, '').trim() : ''),
        el: fs,
        buttons: Array.from(fs.querySelectorAll('[role="radio"]'))
      }));
      const optionCount = (this.variants[0].options || []).length;
      this.hasHiddenOption = this.steps.length < optionCount && !(optionCount === 1 && this.variants.length === 1);
      this.priceMin = Math.min(...this.variants.map((v) => v.price));

      const start = this.variants.find((v) => v.id === cfg.selected) || this.variants.find((v) => v.available) || this.variants[0];
      this.sel = start.options.slice();
      this.key = `rtdq:${this.dataset.product}`;
      const saved = store.get(this.key);
      if (saved && Array.isArray(saved.sel) && saved.sel.length === this.sel.length && this.match(saved.sel)) this.sel = saved.sel;
      this.qty = (saved && saved.qty > 0) ? saved.qty : 0;
      if (saved && saved.size && this.sizeButtons.some((b) => b.dataset.size === saved.size)) this.size = saved.size;
      this.typed = (saved && saved.typed) ? saved.typed : null;
      this.typedShort = (saved && saved.typedShort) ? saved.typedShort : null;
      this.designButtons = Array.from(this.querySelectorAll('[data-design]'));
      this.design = (saved && saved.design === 'service') ? 'service' : 'own';

      this.qtyInput = this.$('[data-qty]');
      this.range = this.$('[data-range]');
      this.dialog = this.$('[data-dialog]');
      this.form = this.dialog ? this.dialog.querySelector('form') : null;

      this.steps.forEach((step) => {
        step.buttons.forEach((btn, i) => {
          btn.addEventListener('click', () => this.pick(step, btn.dataset.value));
          btn.addEventListener('keydown', (e) => {
            const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            const next = step.buttons[(i + dir + step.buttons.length) % step.buttons.length];
            this.pick(step, next.dataset.value);
            next.focus();
          });
        });
      });

      this.sizeLong = this.$('[data-size-long]');
      this.sizeShort = this.$('[data-size-short]');
      this.sizeNote = this.$('[data-size-note]');
      if (this.sizeLong) {
        const handler = () => this.applyTypedSize();
        this.sizeLong.addEventListener('input', handler);
        this.sizeLong.addEventListener('blur', handler);
      }
      if (this.sizeShort) this.sizeShort.addEventListener('input', () => this.update());
      if (this.sizeLong && this.typed) this.sizeLong.value = this.typed;
      if (this.sizeShort && this.typedShort) this.sizeShort.value = this.typedShort;

      this.sizeButtons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.pickSize(btn.dataset.size));
        btn.addEventListener('keydown', (e) => {
          const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          const next = this.sizeButtons[(i + dir + this.sizeButtons.length) % this.sizeButtons.length];
          this.pickSize(next.dataset.size);
          next.focus();
        });
      });

      this.designButtons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.pickDesign(btn.dataset.design));
        btn.addEventListener('keydown', (e) => {
          const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          const next = this.designButtons[(i + dir + this.designButtons.length) % this.designButtons.length];
          this.pickDesign(next.dataset.design);
          next.focus();
        });
      });
      this.syncDesign();

      this.qtyInput.addEventListener('input', () => { const v = parseInt(this.qtyInput.value, 10); if (v > 0) this.setQty(v, false, true); });
      this.qtyInput.addEventListener('blur', () => { if (!(parseInt(this.qtyInput.value, 10) > 0)) this.setQty(this.minQty()); });
      this.range.addEventListener('input', () => this.setQty(this.fromRange(this.range.value), true, false));
      this.$('[data-dec]').addEventListener('click', () => this.setQty(Math.max(1, this.qty - this.stepSize())));
      this.$('[data-inc]').addEventListener('click', () => this.setQty(this.qty + this.stepSize()));
      this.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => this.open()));
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
      if (this.dialog) {
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
        this.dialog.addEventListener('close', () => document.documentElement.classList.remove('rtdq-lock'));
      }
      if (this.form) this.bindForm();

      this.renderFromPrices();
      this.applyVariant(true);
      if (this.sizeLong && this.sizeLong.value) this.applyTypedSize();
      this.bindBar();

      if (this.$('[data-success]') || this.$('.rtdq__error')) this.showPosted();
    }

    shownIdx() { return this.steps.map((s) => s.index); }
    match(sel) { const idx = this.shownIdx(); return this.variants.find((v) => idx.every((i) => v.options[i] === sel[i])); }

    pick(step, value) {
      const next = this.sel.slice();
      next[step.index] = value;
      if (!this.match(next)) {
        const alt = this.variants.find((v) => v.options[step.index] === value && v.available) || this.variants.find((v) => v.options[step.index] === value);
        if (alt) this.shownIdx().forEach((i) => { next[i] = alt.options[i]; });
      }
      const prevMin = this.minQty();
      this.sel = next;
      this.applyVariant(false, prevMin);
    }

    get variant() { return this.match(this.sel) || this.variants[0]; }

    sizeValues() {
      return this.sizeButtons.map((b) => ({ label: b.dataset.size, mm: parseFloat(String(b.dataset.size).replace(',', '.')) || 0 }))
        .filter((x) => x.mm > 0).sort((a, b) => a.mm - b.mm);
    }

    applyTypedSize() {
      if (!this.sizeLong) return;
      const raw = parseFloat(String(this.sizeLong.value).replace(',', '.'));
      const list = this.sizeValues();
      if (!(raw > 0) || !list.length) {
        this.typed = null;
        this.overSize = false;
        if (this.sizeNote) this.sizeNote.textContent = '';
        this.applyVariant(false, this.minQty());
        return;
      }
      this.typed = raw;
      const hit = list.find((x) => x.mm >= raw - 0.001);
      this.overSize = !hit;
      if (hit) {
        const prevMin = this.minQty();
        this.size = hit.label;
        this.sizeButtons.forEach((b) => {
          const on = b.dataset.size === this.size;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
        if (this.sizeNote) {
          this.sizeNote.textContent = Math.abs(hit.mm - raw) < 0.001
            ? ''
            : `Preis für ${hit.label} – die nächste Größe über ${this.fmtNum(raw)} ${this.sizeUnit}.`;
        }
        this.applyVariant(false, prevMin);
      } else {
        if (this.sizeNote) this.sizeNote.textContent = `Über ${list[list.length - 1].label} erstellen wir Euch ein persönliches Angebot.`;
        this.applyVariant(false, this.minQty());
      }
    }

    fmtNum(v) { return String(Math.round(v * 10) / 10).replace('.', ','); }

    measureLabel() {
      if (!this.typed) return '';
      const short = this.sizeShort ? parseFloat(String(this.sizeShort.value).replace(',', '.')) : 0;
      return short > 0
        ? `${this.fmtNum(this.typed)} × ${this.fmtNum(short)} ${this.sizeUnit}`
        : `${this.fmtNum(this.typed)} ${this.sizeUnit}`;
    }

    shippingFor(q) {
      if (!this.shipping.length) return null;
      let cur = this.shipping[0];
      this.shipping.forEach((x) => { if (q >= x.min) cur = x; });
      return cur;
    }

    pickSize(value) {
      if (!value) return;
      const prevMin = this.minQty();
      this.size = value;
      this.overSize = false;
      this.typed = null;
      if (this.sizeLong) this.sizeLong.value = '';
      if (this.sizeNote) this.sizeNote.textContent = '';
      this.applyVariant(false, prevMin);
    }

    sectionFor(v) {
      if (!this.grid.length) return null;
      const opts = v.options.map((o) => String(o).toLowerCase());
      let best = null;
      this.grid.forEach((sec) => {
        if (!sec.terms.length) return;
        const hit = opts.includes(sec.full) || sec.terms.every((t) => opts.includes(t));
        const weight = opts.includes(sec.full) ? sec.terms.length + 1 : sec.terms.length;
        if (hit && (!best || weight >= best._w)) { best = sec; best._w = weight; }
      });
      return best || this.grid.find((sec) => !sec.terms.length) || null;
    }

    resolve(sec, depth) {
      if (!sec) return null;
      if (!sec.derive) return { cols: sec.cols, rows: sec.rows, factor: 1 };
      if ((depth || 0) > 4) return { cols: sec.cols, rows: sec.rows, factor: 1 };
      const src = this.grid.find((x) => x.full === sec.derive.from || x.terms.join(' + ') === sec.derive.from || x.terms.includes(sec.derive.from));
      const base = this.resolve(src, (depth || 0) + 1);
      if (!base) return { cols: sec.cols, rows: sec.rows, factor: 1 };
      return { cols: sec.cols.length ? sec.cols : base.cols, rows: Object.keys(sec.rows).length ? sec.rows : base.rows, factor: (sec.derive.factor || 1) * base.factor };
    }

    gridTiersFor(v, size) {
      const sec = this.sectionFor(v);
      const res = this.resolve(sec);
      if (!res || !res.cols.length) return null;
      const key = String(size || this.size || '').toLowerCase();
      let row = res.rows[key];
      if (!row) {
        const fallback = this.grid.find((x) => !x.terms.length);
        const fres = this.resolve(fallback);
        if (fres && fres.rows[key]) return gridTiers(fallback, fres.cols, fres.rows[key]).map((t) => ({ min: t.min, price: Math.round(t.price * fres.factor * 100) / 100 }));
        return [];
      }
      return gridTiers(sec, res.cols, row).map((t) => ({ min: t.min, price: Math.round(t.price * res.factor * 100) / 100 }));
    }

    tiersFor(v, size) {
      if (this.grid.length) {
        const g = this.gridTiersFor(v, size);
        if (g) return g;
      }
      const opts = v.options.map((o) => String(o).toLowerCase());
      let best = null;
      this.pricing.overrides.forEach((o) => {
        if (o.terms.every((t) => opts.includes(t)) && (!best || o.terms.length >= best.terms.length)) best = o;
      });
      if (best && best.tiers.length) return best.tiers;
      const surcharge = this.hasHiddenOption ? 0 : (v.price - this.priceMin) / 100;
      if (this.pricing.base.length) return this.pricing.base.map((x) => ({ min: x.min, price: Math.round((x.price + surcharge) * 100) / 100 }));
      return v.price > 0 ? [{ min: 1, price: v.price / 100 }] : [];
    }

    isQuote(v, size) {
      const opts = v.options.map((o) => String(o).toLowerCase());
      if (this.quoteValues.includes(String(size || this.size || '').toLowerCase())) return true;
      return this.quoteValues.some((q) => opts.includes(q)) || !this.tiersFor(v, size).length;
    }

    pickDesign(value) {
      this.design = value === 'service' ? 'service' : 'own';
      this.syncDesign();
      this.update();
    }

    syncDesign() {
      this.designButtons.forEach((b) => {
        const on = b.dataset.design === this.design;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
    }

    designActive() {
      return this.designButtons.length > 0 && this.design === 'service';
    }

    hasNoMold() {
      if (!this.moldTerm || !this.noMold.length) return false;
      const hay = `${this.productInfo} ${(this.sel || []).join(' ')}`.toLowerCase();
      return this.noMold.some((k) => hay.includes(k));
    }

    withoutMold(item) {
      if (!this.hasNoMold()) return item;
      const t = this.moldTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return String(item)
        .replace(new RegExp(`\\s*(?:&|\\+|und|,)\\s*${t}`, 'i'), '')
        .replace(new RegExp(`${t}\\s*(?:&|\\+|und|,)\\s*`, 'i'), '')
        .replace(new RegExp(t, 'i'), '')
        .trim();
    }

    renderFromPrices() {
      if (this.sizeButtons.length) {
        const v = this.variant;
        this.sizeButtons.forEach((btn) => {
          const el = btn.querySelector('[data-from]');
          if (!el) return;
          const size = btn.dataset.size;
          const tiers = this.isQuote(v, size) ? [] : this.tiersFor(v, size);
          el.textContent = tiers.length ? `ab ${this.fmt.format(Math.min(...tiers.map((t) => t.price)))}` : 'auf Anfrage';
        });
      }
      this.steps.forEach((step) => {
        step.buttons.forEach((btn) => {
          const vs = this.variants.filter((v) => v.options[step.index] === btn.dataset.value);
          const priced = vs.filter((v) => !this.isQuote(v));
          const el = btn.querySelector('[data-from]');
          if (!el) return;
          if (!priced.length) { el.textContent = 'auf Anfrage'; return; }
          const min = Math.min(...priced.map((v) => Math.min(...this.tiersFor(v).map((t) => t.price))));
          el.textContent = `ab ${this.fmt.format(min)}`;
        });
        const texts = step.buttons.map((b) => (b.querySelector('[data-from]') || {}).textContent || '');
        if (texts.length > 1 && texts.every((x) => x === texts[0])) step.buttons.forEach((b) => { const el = b.querySelector('[data-from]'); if (el) el.textContent = ''; });
      });
    }

    applyVariant(initial, prevMin) {
      const v = this.variant;
      this.tiers = (this.overSize || this.isQuote(v)) ? [] : this.tiersFor(v);
      if (this.sizeButtons.length) {
        this.sizeButtons.forEach((btn) => {
          const on = btn.dataset.size === this.size;
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
          btn.tabIndex = on ? 0 : -1;
        });
        if (!initial) this.renderFromPrices();
      }
      this.steps.forEach((step) => {
        step.buttons.forEach((btn) => {
          const on = this.sel[step.index] === btn.dataset.value;
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
          btn.tabIndex = on ? 0 : -1;
          const test = this.sel.slice(); test[step.index] = btn.dataset.value;
          btn.classList.toggle('is-unavailable', !this.match(test));
        });
      });
      const min = this.minQty();
      if (!this.qty || (initial && this.qty < 1)) this.qty = min;
      if (!initial && (this.qty < min || this.qty === prevMin)) this.qty = min;
      this.renderTiers();
      this.setQty(this.qty);
      this.syncCart(v);
    }

    minQty() { return this.tiers && this.tiers.length ? this.tiers[0].min : 1; }
    stepSize() { const q = this.qty; return q < 50 ? 5 : q < 200 ? 10 : q < 1000 ? 50 : 100; }

    toRange(q) {
      const min = Math.max(1, this.minQty()); const max = Math.max(min + 1, this.max);
      return Math.round(Math.log(Math.min(Math.max(q, min), max) / min) / Math.log(max / min) * 1000);
    }
    fromRange(p) {
      const min = Math.max(1, this.minQty()); const max = Math.max(min + 1, this.max);
      let q = min * Math.pow(max / min, p / 1000);
      const r = q < 50 ? 1 : q < 200 ? 5 : q < 1000 ? 10 : 50;
      q = Math.round(q / r) * r;
      return Math.min(max, Math.max(min, q));
    }

    tierFor(q) {
      if (!this.tiers.length) return null;
      let cur = this.tiers[0]; this.tiers.forEach((x) => { if (q >= x.min) cur = x; });
      return cur;
    }

    renderTiers() {
      const presets = this.$('[data-presets]');
      presets.innerHTML = '';
      const min = this.minQty();
      const source = this.presets.length ? this.presets : this.tiers.map((t) => t.min).concat([this.max]);
      const vals = [...new Set(source)].filter((v) => v >= min && v <= this.max).slice(0, 8);
      if (this.tiers.length && (!vals.length || vals[0] !== min)) vals.unshift(min);
      vals.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'rtdq__preset'; b.textContent = v; b.dataset.v = v;
        b.addEventListener('click', () => this.setQty(v));
        presets.appendChild(b);
      });
      const rows = this.$('[data-tier-rows]');
      if (!rows) return;
      rows.innerHTML = '';
      if (!this.tiers.length) { rows.innerHTML = '<tr><td colspan="2">Preis auf Anfrage</td></tr>'; return; }
      this.tiers.forEach((x, i) => {
        const next = this.tiers[i + 1];
        const tr = document.createElement('tr');
        tr.innerHTML = `<td>${next ? `${x.min}–${next.min - 1} Stück` : `ab ${x.min} Stück`}</td><td>${this.fmt.format(x.price)}</td>`;
        tr.tabIndex = 0; tr.dataset.min = x.min;
        tr.addEventListener('click', () => this.setQty(x.min));
        tr.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setQty(x.min); } });
        rows.appendChild(tr);
      });
    }

    setQty(q, syncInput = true, syncRange = true) {
      this.qty = Math.max(1, Math.min(Math.round(q), 99999));
      if (syncInput) this.qtyInput.value = this.qty;
      if (syncRange) this.range.value = this.toRange(this.qty);
      this.update();
    }

    totalLabel() {
      const el = this.$('[data-total-label]');
      return el ? el.textContent.trim() : 'Gesamt';
    }

    sizeLabel() {
      if (!this.sizeStep) return '';
      const legend = this.sizeStep.querySelector('legend');
      const text = legend ? legend.textContent.replace(/^\s*\d+\s*/, '').trim() : 'Größe';
      return text || 'Größe';
    }

    variantLabel() {
      const parts = this.steps.map((s) => `${s.name}: ${this.sel[s.index]}`);
      const measure = this.measureLabel();
      if (measure) parts.push(`${this.sizeLabel()}: ${measure} (Preis: ${this.size})`);
      else if (this.size) parts.push(`${this.sizeLabel()}: ${this.size}`);
      return parts.join(' · ');
    }

    update() {
      const q = this.qty;
      const tier = this.tierFor(q);
      const min = this.minQty();
      const onRequest = !tier;
      const unit = tier ? tier.price : 0;
      const count = tier && q < min ? min : q;
      const total = unit * count;
      const below = tier && q < min;
      const warn = this.$('[data-warn]');

      const above = q > this.max;
      this.$('[data-min-note]').textContent = tier ? `Mindestmenge ${min} Stück` : '';
      warn.hidden = !(below || above);
      if (below) warn.textContent = `Mindestmenge für diese Ausführung: ${min} Stück. Kleinere Mengen gern auf Anfrage.`;
      else if (above) warn.textContent = this.quoteAbove || `Über ${this.max} Stück erstellen wir Euch ein persönliches Angebot.`;

      const ship = onRequest ? null : this.shippingFor(count);
      const shipCost = ship ? ship.price : 0;

      this.$('[data-total]').textContent = onRequest ? 'Auf Anfrage' : this.fmt.format(unit);
      this.$('[data-unit]').textContent = onRequest
        ? 'Für diese Ausführung erstellen wir Euch ein individuelles Angebot.'
        : `${this.totalLabel()}: ${this.fmt.format(total)} · ${count} Stück · inkl. MwSt.`;

      const inc = this.$('[data-included]');
      const dyn = [];
      this.steps.forEach((step) => {
        if (!this.freeOptionNames.includes(String(step.name).toLowerCase())) return;
        const value = this.sel[step.index];
        if (value) dyn.push(value);
      });
      const dynLower = dyn.map((x) => String(x).toLowerCase());
      const freeValues = [];
      this.steps.forEach((step) => {
        if (!this.freeOptionNames.includes(String(step.name).toLowerCase())) return;
        step.buttons.forEach((b) => freeValues.push(String(b.dataset.value || '').toLowerCase()));
      });
      const shipLower = ship ? String(this.shippingLabel).toLowerCase() : '';
      const items = this.included
        .map((x) => this.withoutMold(x))
        .filter(Boolean)
        .filter((x) => !dynLower.includes(String(x).toLowerCase()))
        .filter((x) => !freeValues.includes(String(x).toLowerCase()))
        .filter((x) => !shipLower || String(x).toLowerCase() !== shipLower)
        .map((x) => [x, '0,00 €'])
        .concat(dyn.map((x) => [x, '0,00 €']));
      if (this.designActive()) items.push([this.designRow, this.designPrice > 0 ? this.fmt.format(this.designPrice) : '0,00 €', this.designPrice > 0]);
      if (ship) items.push([this.shippingLabel, shipCost > 0 ? this.fmt.format(shipCost) : this.shippingFree, shipCost > 0]);
      inc.innerHTML = items.map(([k, v, cost]) => `<li${cost ? ' class="is-cost"' : ''}><span>${this.esc(k)}</span><strong translate="no">${this.esc(v)}</strong></li>`).join('');

      const nudge = this.$('[data-nudge]');
      nudge.hidden = true;
      if (tier && !below) {
        const next = this.tiers[this.tiers.indexOf(tier) + 1];
        if (next) {
          const nextTotal = next.min * next.price;
          const pct = Math.round((1 - next.price / unit) * 100);
          if (nextTotal <= total) {
            nudge.innerHTML = `<strong>Spartipp:</strong> ${next.min} Stück kosten nur ${this.fmt.format(nextTotal)} – ${this.fmt.format(total - nextTotal)} weniger für ${next.min - q} Patches mehr. <button type="button" data-jump="${next.min}">Auf ${next.min} Stück</button>`;
            nudge.hidden = false;
          } else if (next.min - q <= Math.max(10, q * 0.4)) {
            nudge.innerHTML = `Noch ${next.min - q} Stück bis zur nächsten Staffel: nur ${this.fmt.format(next.price)} pro Patch (−${pct} %). <button type="button" data-jump="${next.min}">Auf ${next.min} Stück</button>`;
            nudge.hidden = false;
          }
          const jb = nudge.querySelector('[data-jump]');
          if (jb) jb.addEventListener('click', () => this.setQty(parseInt(jb.dataset.jump, 10)));
        }
      }

      this.querySelectorAll('.rtdq__preset').forEach((b) => b.setAttribute('aria-pressed', parseInt(b.dataset.v, 10) === q ? 'true' : 'false'));
      this.querySelectorAll('[data-tier-rows] tr').forEach((tr) => tr.classList.toggle('is-current', !!tier && !below && parseInt(tr.dataset.min, 10) === tier.min));

      const barTotal = this.$('[data-bar-total]');
      if (barTotal) {
        barTotal.textContent = onRequest ? 'Auf Anfrage' : this.fmt.format(total);
        const parts = [`${count} Stück`];
        if (this.size) parts.push(this.measureLabel() || this.size);
        this.$('[data-bar-meta]').textContent = parts.join(' · ');
      }

      store.set(this.key, { sel: this.sel, qty: this.qty, size: this.size, typed: this.typed, typedShort: this.sizeShort ? this.sizeShort.value : null, design: this.design });
      const prev = this.summary || {};
      this.summary = {
        ref: prev.ref,
        product: this.dataset.product,
        variant: this.variantLabel() || this.variant.title,
        options: this.steps.map((s) => [s.name, this.sel[s.index]])
          .concat(this.measureLabel() ? [['Maße', this.measureLabel()]] : (this.size ? [[this.sizeLabel(), this.size]] : []))
          .concat(this.designButtons.length ? [[this.designLabel, this.designActive() ? this.designService : this.designOwn]] : []),
        size: this.size || '',
        measure: this.measureLabel(),
        qty: String(count),
        price: onRequest
          ? 'auf Anfrage'
          : `${this.fmt.format(total)} (${this.fmt.format(unit)}/Stück, inkl. MwSt.)${ship ? ` + ${this.shippingLabel} ${shipCost > 0 ? this.fmt.format(shipCost) : this.shippingFree}` : ''}${this.designActive() && this.designPrice > 0 ? ` + ${this.designRow} ${this.fmt.format(this.designPrice)}` : ''}`,
        url: `${this.dataset.url}${this.dataset.url.includes('?') ? '&' : '?'}variant=${this.variant.id}`
      };
      this.syncCartQty(count);
    }

    syncCart(v) {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== String(v.id)) { url.searchParams.set('variant', v.id); window.history.replaceState({}, '', url); }
      } catch (e) {}
      if (this.dataset.syncCart !== 'true') return;
      const scope = this.closest('.shopify-section') || document;
      scope.querySelectorAll('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]').forEach((el) => {
        el.value = v.id;
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    syncCartQty(count) {
      if (this.dataset.syncCart !== 'true') return;
      const scope = this.closest('.shopify-section') || document;
      scope.querySelectorAll('form[action*="/cart/add"] input[name="quantity"]').forEach((el) => { el.value = count; });
      if (!this.size) return;
      const name = `properties[${this.sizeLabel()}]`;
      scope.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = this.size;
      });
    }

    ref() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
      return `RTD-${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${rand}`;
    }

    open() {
      if (!this.dialog) return;
      this.summary.ref = this.summary.ref || this.ref();
      const grid = this.$('[data-summary-grid]');
      if (grid) {
        const items = this.summary.options.concat([['Menge', `${this.summary.qty} Stück`]]);
        grid.innerHTML = items.map(([k, v]) => `<div><small>${this.esc(k)}</small><strong>${this.esc(v)}</strong></div>`).join('');
        this.$('[data-summary-total]').textContent = this.summary.price.split(' (')[0];
      }
      this.querySelectorAll('[data-f]').forEach((el) => { const k = el.dataset.f; if (k in this.summary) el.value = this.summary[k]; });
      document.documentElement.classList.add('rtdq-lock');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      const first = this.dialog.querySelector('input:not([type="hidden"]), textarea, .rtd-btn');
      if (first) first.focus({ preventScroll: true });
    }

    bindForm() {
      const saved = store.get('rtdq:fields') || {};
      this.form.querySelectorAll('[data-persist]').forEach((el) => {
        if (!el.value && saved[el.dataset.persist]) el.value = saved[el.dataset.persist];
        el.addEventListener('input', () => {
          const all = store.get('rtdq:fields') || {};
          all[el.dataset.persist] = el.value; store.set('rtdq:fields', all);
        });
      });
      this.form.addEventListener('submit', () => {
        const f = this.form.querySelector('[data-persist="first"]');
        const l = this.form.querySelector('[data-persist="last"]');
        const name = this.form.querySelector('[data-f="name"]');
        if (name) name.value = `${f ? f.value : ''} ${l ? l.value : ''}`.trim();
        this.querySelectorAll('[data-f]').forEach((el) => { const k = el.dataset.f; if (k in this.summary) el.value = this.summary[k]; });
        store.set('rtdq:last', this.summary);
      });
    }

    showPosted() {
      const last = store.get('rtdq:last') || {};
      if (last.ref) this.summary = Object.assign({}, this.summary, last);
      const refEl = this.$('[data-success-ref]');
      if (refEl) {
        refEl.textContent = last.ref || '–';
        const mail = this.$('[data-mailto]');
        if (mail) {
          const subject = `Design-Datei zu Anfrage ${last.ref || ''} – ${last.product || this.dataset.product}`;
          const body = `Hallo ${this.dataset.shop}-Team,\n\nanbei unsere Design-Datei zur Anfrage ${last.ref || ''}.\n\nProdukt: ${last.product || ''}\nAusführung: ${last.variant || ''}\nMenge: ${last.qty || ''} Stück\n\nViele Grüße`;
          mail.href = `mailto:${this.dataset.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }
        const fields = store.get('rtdq:fields') || {};
        delete fields.motif; delete fields.date; delete fields.exact; store.set('rtdq:fields', fields);
      } else if (last.ref) {
        this.summary.ref = last.ref;
      }
      window.requestAnimationFrame(() => {
        if (!this.dialog) return;
        document.documentElement.classList.add('rtdq-lock');
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
        if (refEl) this.querySelectorAll('[data-f]').forEach(() => {});
      });
    }

    bindBar() {
      const bar = this.$('[data-bar]');
      const cta = this.$('.rtdq__cta');
      if (!bar || !cta || !('IntersectionObserver' in window)) return;
      let ctaVisible = true; let pastTop = false;
      const sync = () => {
        const show = !ctaVisible && pastTop;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        bar.querySelector('button').tabIndex = show ? 0 : -1;
      };
      new IntersectionObserver(([e]) => { ctaVisible = e.isIntersecting; pastTop = e.boundingClientRect.top < 0 || e.isIntersecting ? e.boundingClientRect.top < 0 : pastTop; sync(); }).observe(cta);
      window.addEventListener('scroll', () => { const r = cta.getBoundingClientRect(); pastTop = r.bottom < 0; sync(); }, { passive: true });
    }

    esc(v) { return String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  }

  customElements.define('rtd-quote', RtdQuote);
})();
